import React, { useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { SignIn } from "@phosphor-icons/react";
import { PasswordInput } from "../components/PasswordInput";
import { GoogleSignInButton, AuthDivider } from "../components/GoogleSignInButton";
import { offerInstallAfterLogin } from "../components/InstallActions";
import { WarmupOverlay } from "../components/WarmupOverlay";
import { isBackendWarm, markBackendWarm } from "../lib/warmup";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [warming, setWarming] = useState(false);
  const warmupTimer = useRef(null);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    // If we haven't confirmed the backend is warm, arm a 2s timer to show the overlay.
    if (!isBackendWarm()) {
      warmupTimer.current = setTimeout(() => setWarming(true), 2000);
    }
    try {
      await login(email, password);
      markBackendWarm();
      toast.success("Welcome back");
      offerInstallAfterLogin();
      navigate("/fixtures");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally {
      if (warmupTimer.current) clearTimeout(warmupTimer.current);
      setWarming(false);
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] grid place-items-center px-4 py-16 grid-bg">
      <WarmupOverlay active={warming} />
      <div className="w-full max-w-md surface border border-white/10 p-8">
        <div className="text-[10px] uppercase tracking-[0.3em] text-primary-yellow font-bold">Sign in</div>
        <h1 className="font-heading text-4xl uppercase font-black mt-2">Back on the pitch</h1>
        <form onSubmit={submit} className="mt-8 space-y-5" data-testid="login-form">
          <GoogleSignInButton label="Sign in with Google" />
          <AuthDivider />
          <Field label="Email">
            <input
              data-testid="login-email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full bg-[#0B0D11] border border-white/10 px-4 py-3 text-white focus:border-primary-yellow focus:outline-none focus:ring-1 focus:ring-primary-yellow font-mono-num"
            />
          </Field>
          <Field label="Password">
            <PasswordInput
              testid="login-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
            />
          </Field>
          <button
            data-testid="login-submit"
            type="submit"
            disabled={loading}
            className="w-full bg-primary-yellow text-black font-bold uppercase tracking-[0.2em] py-4 text-sm hover:-translate-y-0.5 transition-transform disabled:opacity-50 flex items-center justify-center gap-2"
          >
            <SignIn size={16} weight="bold" />
            {loading ? "Signing in…" : "Sign in"}
          </button>
        </form>
        <div className="mt-6 text-sm text-white/60 flex items-center justify-between">
          <Link data-testid="link-forgot" to="/forgot-password" className="text-white/60 hover:text-primary-yellow">Forgot password?</Link>
          <Link data-testid="link-to-register" to="/register" className="text-primary-yellow hover:underline">
            Create an account
          </Link>
        </div>
      </div>
    </div>
  );
}

const Field = ({ label, children }) => (
  <div>
    <label className="block text-[10px] uppercase tracking-[0.2em] text-white/50 mb-2 font-semibold">{label}</label>
    {children}
  </div>
);
